import { useCallback, useEffect, useReducer } from 'react'
import { useLocation } from 'react-router-dom'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { hasOverlayAbove } from '@/utils/overlayLayers'

type Mode = 'collapsed' | 'expanded' | 'pinned'
type Action = 'toggle' | 'expand' | 'close' | 'pin' | 'navigate'

function transition(mode: Mode, action: Action): Mode {
  switch (action) {
    case 'toggle':
      return mode === 'collapsed' ? 'expanded' : 'collapsed'
    case 'expand':
      return mode === 'pinned' ? mode : 'expanded'
    case 'close':
      return 'collapsed'
    case 'pin':
      return mode === 'pinned' ? 'expanded' : 'pinned'
    case 'navigate':
      return mode === 'pinned' ? mode : 'collapsed'
  }
}

/** One layout-owned state machine. Every fresh mount starts collapsed. */
export function useIslandNavigation() {
  const [mode, dispatch] = useReducer(transition, 'collapsed')
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { pathname } = useLocation()

  useEffect(() => {
    dispatch('navigate')
  }, [pathname])
  // A desktop pin must never turn into a permanently open mobile modal.
  useEffect(() => {
    dispatch('close')
  }, [isDesktop])

  const close = useCallback(() => dispatch('close'), [])
  const select = useCallback(() => dispatch('navigate'), [])
  const expand = useCallback(() => {
    if (!hasOverlayAbove('float')) dispatch('expand')
  }, [])
  const toggle = useCallback(() => {
    if (!hasOverlayAbove('float')) dispatch('toggle')
  }, [])
  const pin = useCallback(() => {
    if (isDesktop) dispatch('pin')
  }, [isDesktop])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.altKey) return
      if (event.key !== '\\' || (!event.metaKey && !event.ctrlKey)) return
      if (hasOverlayAbove('float')) return
      event.preventDefault()
      toggle()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  return {
    mode,
    isDesktop,
    open: mode !== 'collapsed',
    pinned: mode === 'pinned' && isDesktop,
    close,
    select,
    expand,
    toggle,
    pin,
  }
}
